import React from 'react';
import {withRouter} from 'react-router';
import PropTypes from 'prop-types';
import moment from 'moment';
import {renderDuration, renderMoment} from 'lib/timeUtils';
import {makeNavigateToQuestion} from 'client/Classrooms/ClassroomUtil';
import {setUpStore} from 'client/framework';
import {mandarkEndpoint} from '@albert-io/json-api-framework/request/builder/legacy';
import MarkdownRendererV2 from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';
import PreviousAttempts from 'generic/PreviousAttempts/PreviousAttempts.react';
import QuestionDifficultyLabel from 'generic/QuestionDifficultyLabel/QuestionDifficultyLabel.react';
import Button from 'sg/Button/Button.react';
import SettingsTableStore from 'sg/Tables/SettingsTable/SettingsTable.store';
import SettingsTable from 'sg/Tables/SettingsTable/SettingsTable.react';
import {Column} from 'sg/Tables/MandarkTable/MandarkTable.react';

import EmptyInsightsReport from '../../EmptyInsightsReport/EmptyInsightsReport.react';

const settingsTableStore = setUpStore(SettingsTableStore, 'StudentInsightsQuestionsTableStore');

class QuestionsTable extends React.Component {
  static propTypes = {
    location: PropTypes.shape({
      query: PropTypes.shape({
        attempt: PropTypes.string,
        startDate: PropTypes.string,
        endDate: PropTypes.string,
        subjectId: PropTypes.string,
        guideLevelId: PropTypes.string
      })
    }),
    params: PropTypes.shape({
      studentId: PropTypes.string
    })
  };

  getGuessesQuery() {
    const {attempt, startDate, endDate, subjectId, guideLevelId = null} = this.props.location.query;

    return mandarkEndpoint(['students_v2', this.props.params.studentId, 'guesses_v1'])
      .include('question_v2.question_set_v1.guide_levels_v2')
      .include('question_v2.question_set_v1.subject_v2')
      .filter(
        {
          attempt_number: attempt
        },
        attempt !== 'mostRecent'
      )
      .filter(
        {
          inserted_at: {
            greater_than_inclusive: startDate
          }
        },
        startDate
      )
      .filter(
        {
          inserted_at: {
            less_than_inclusive: endDate
          }
        },
        endDate
      )
      .filter(
        {
          question_v2: {
            question_set_v1: {
              subject_v2: subjectId
            }
          }
        },
        subjectId
      )
      .filter(
        {
          question_v2: {
            question_set_v1: {
              guide_levels_v2: guideLevelId
            }
          }
        },
        guideLevelId
      );
  }

  getMostRecentGuessesQuery() {
    return this.getGuessesQuery()
      .filter({
        'meta.is_most_recent_guess': true
      })
      .customQuery({
        with_meta: 'guess_v1'
      })
      .meta({
        context: {
          guess: {
            most_recent: true
          }
        }
      });
  }

  getSearchFilter(query, term) {
    return query.filter({
      question_v2: {
        title: {
          case_insensitive_substring: term
        }
      }
    });
  }

  renderDifficulty(guess) {
    return (
      <QuestionDifficultyLabel
        className='student-insights-table__difficulty'
        difficulty={guess.getQuestion().getDifficulty()}
        short
      />
    );
  }

  renderMadeAt(guess) {
    return renderMoment(moment(guess.getInsertedAt()), 'dateTime');
  }

  renderResult = (guess) => {
    /**
     * If 'Most Recent' is selected for the attempt, we want to include attempts within the selected date range
     * If a specific attempt is selected, we want to only include that specific attempt
     */
    const {attempt} = this.props.location.query;
    const isMostRecentAttempt = attempt === 'mostRecent';
    let {startDate} = this.props.location.query;
    startDate = isMostRecentAttempt && startDate ? startDate : null;
    let {endDate} = this.props.location.query;
    endDate = isMostRecentAttempt && endDate ? endDate : null;
    const tooltipHeader = isMostRecentAttempt ? undefined : 'Attempt details';

    return (
      <PreviousAttempts
        specificAttemptNumberToShow={isMostRecentAttempt ? null : attempt}
        initialIsCorrectStatus={guess.isCorrect()}
        questionId={guess.getQuestion().getId()}
        tooltipHeader={tooltipHeader}
        showTooltip
        storeName={`student-drilldown-${guess.getQuestion().getId()}`}
        userId={this.props.params.studentId}
        startDate={startDate}
        endDate={endDate}
        disableAttemptLink
      />
    );
  };

  renderTimeSpent(guess) {
    const timeSpent = moment.duration(guess.getTimeElapsed());
    const formattedTimeSpent = renderDuration(timeSpent, {style: 'hh:mm:ss'});
    return formattedTimeSpent;
  }

  renderTitle(guess) {
    return <MarkdownRendererV2 text={guess.getQuestion().getTitle()} />;
  }

  renderViewQuestionsButton = (guess) => {
    const questionSetSlug = guess.getQuestion().getQuestionSet().getSlugId();
    const questionSlug = guess.getQuestion().getSlugId();
    return (
      <Button
        text='View question'
        whiteBg
        small
        onClick={(e) => {
          e.stopPropagation();
          makeNavigateToQuestion(questionSetSlug, questionSlug)();
        }}
      />
    );
  };

  render() {
    const emptyResultsHeader = settingsTableStore.getSearchTerm()
      ? `This student hasn't answered any questions matching your search term.`
      : `This student hasn't answered any questions yet.`;

    const emptyResultsContent = settingsTableStore.getSearchTerm()
      ? 'Once the student has answered at least 1 question matching your search term, answered questions will appear here.' // eslint-disable-line max-len
      : 'Once this student has answered at least 1 question, answered questions will appear here.';

    const query =
      this.props.location.query.attempt === 'mostRecent'
        ? this.getMostRecentGuessesQuery()
        : this.getGuessesQuery();

    return (
      <SettingsTable
        emptyTableElement={
          <EmptyInsightsReport header={emptyResultsHeader} content={emptyResultsContent} />
        }
        query={query}
        searchFilters={this.getSearchFilter}
        storeName={settingsTableStore.getName()}
      >
        <Column
          align='center'
          defaultVisible
          content={this.renderDifficulty}
          heading='Difficulty'
          sortBy='question_v2.difficulty'
        />
        <Column
          content={this.renderTitle}
          defaultSortParam
          defaultVisible
          heading='Title'
          sortBy='question_v2.title'
        />
        <Column
          align='right'
          content={this.renderTimeSpent}
          defaultVisible
          heading='Time Spent'
          sortBy='time_elapsed'
        />
        <Column
          align='center'
          defaultVisible
          heading='Made At'
          content={this.renderMadeAt}
          sortBy='inserted_at'
        />
        <Column
          align='center'
          content={this.renderResult}
          defaultVisible
          heading='Result'
          sortBy='correct'
        />
        <Column alwaysVisible align='center' content={this.renderViewQuestionsButton} />
      </SettingsTable>
    );
  }
}

export default withRouter(QuestionsTable);
